import { Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Toolbar, Box, Divider, Typography, Button, useMediaQuery, useTheme, Avatar, Switch } from '@mui/material';
import { 
  Dashboard, 
  PointOfSale, 
  Receipt, 
  Inventory, 
  People, 
  LocalShipping,
  Assessment,
  Settings,
  Store,
  RequestQuote,
  Person,
  BarChart,
  DarkMode,
  LightMode,
  Logout,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { useState, useEffect } from 'react';

const DRAWER_WIDTH = 260;

// Menú para modo POS
const posMenuItems = [
  { text: 'Transacciones', icon: <PointOfSale />, path: '/pos' },
  { text: 'Productos', icon: <Inventory />, path: '/inventory' },
  { text: 'Clientes', icon: <People />, path: '/customers' },
  { text: 'Proveedores', icon: <LocalShipping />, path: '/suppliers' },
  { text: 'Inventario', icon: <Store />, path: '/stock' },
  { text: 'Cotizaciones', icon: <RequestQuote />, path: '/quotes' },
  { text: 'Empleados', icon: <Person />, path: '/employees' },
  { text: 'Estadísticas', icon: <BarChart />, path: '/statistics' },
  { text: 'Configuración', icon: <Settings />, path: '/settings' },
  { text: 'Sucursales', icon: <Store />, path: '/branches' },
];

// Menú para modo FACTURACION
const billingMenuItems = [
  // Ventas
  { text: 'Consumidor Fisnal', icon: <Receipt />, path: '/billing/consumidor-final', category: 'VENTAS' },
  { text: 'CCF', icon: <Receipt />, path: '/billing/ccf', category: 'VENTAS' },
  { text: 'Comp. Retención', icon: <Receipt />, path: '/billing/retencion', category: 'VENTAS' },
  { text: 'Sujeto Excluido', icon: <Receipt />, path: '/billing/sujeto-excluido', category: 'VENTAS' },
  { text: 'Nota Crédito', icon: <Receipt />, path: '/billing/nota-credito', category: 'VENTAS' },
  { text: 'Nota Débito', icon: <Receipt />, path: '/billing/nota-debito', category: 'VENTAS' },
  // Compras
  { text: 'Compras CF', icon: <Store />, path: '/billing/compras-cf', category: 'COMPRAS' },
  { text: 'Compras CCF', icon: <Store />, path: '/billing/compras-ccf', category: 'COMPRAS' },
  // Libros
  { text: 'Libro Compras', icon: <Assessment />, path: '/billing/libro-compras', category: 'LIBROS' },
  { text: 'Libro Ventas CF', icon: <Assessment />, path: '/billing/libro-ventas-cf', category: 'LIBROS' },
  { text: 'Libro Ventas CCF', icon: <Assessment />, path: '/billing/libro-ventas-ccf', category: 'LIBROS' },
  { text: 'Libro Diario', icon: <Assessment />, path: '/billing/libro-diario', category: 'LIBROS' },
  { text: 'Diario Mayor', icon: <Assessment />, path: '/billing/libro-mayor', category: 'LIBROS' },
  { text: 'Estados Financieros', icon: <Assessment />, path: '/billing/estados-financieros', category: 'LIBROS' },
  // Reportes MH
  { text: 'F07 - IVA', icon: <Assessment />, path: '/billing/f07-iva', category: 'REPORTES MH' },
  { text: 'F-11 - RENTA', icon: <Assessment />, path: '/billing/f11-renta', category: 'REPORTES MH' },
  { text: 'F-14 - P.CTA', icon: <Assessment />, path: '/billing/f14-pcta', category: 'REPORTES MH' },
  // Configuración
  { text: 'Configuración', icon: <Settings />, path: '/settings', category: 'OTROS' },
];

const Sidebar = ({ isMobileDrawer = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { modo, toggleModo, setModo } = useApp();
  const { logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Obtener estado del modo oscuro desde AppContext
  const { darkMode, toggleDarkMode } = useApp();

  // Obtener módulos autorizados desde localStorage
  const getAuthorizedModules = () => {
    try {
      const stored = localStorage.getItem('nx2026_authorized_modules');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  };

  const authorizedModules = getAuthorizedModules();
  const hasPOS = authorizedModules.includes('POS');
  const hasFacturacion = authorizedModules.includes('FACTURACION');

  // Obtener el logo de la empresa desde los módulos guardados en localStorage
  const [companyLogo, setCompanyLogo] = useState(null);

  useEffect(() => {
    const modulesData = localStorage.getItem('nx2026_modules');
    if (modulesData) {
      try {
        const modules = JSON.parse(modulesData);
        // Buscar la empresa que tiene el módulo de facturación (BILLING en mayúsculas)
        // o el módulo POS
        const targetModule = modules.find(m => 
          m.modules && m.modules.some(mod => {
            const modId = mod.id ? mod.id.toUpperCase() : '';
            const modoUpperCase = (modoActual || modo || 'FACTURACION').toUpperCase();
            return modId.includes(modoUpperCase);
          })
        );
        if (targetModule && targetModule.company) {
          // Priorizar urlLogo, luego logo
          const logoUrl = targetModule.company.urlLogo || targetModule.company.logo;
          if (logoUrl) {
            setCompanyLogo(logoUrl);
          }
        }
      } catch (e) {
        console.error('Error parsing modules:', e);
      }
    }
  }, [modo]);

  // Estado local para evitar stale closure en la navegación
  // Este estado se sincroniza con el contexto pero nos da el valor actual
  const [modoActual, setModoActual] = useState(modo);

  // Sincronizar estado local con el contexto
  useEffect(() => {
    setModoActual(modo);
  }, [modo]);

  // Determinar qué menú mostrar según el modo
  const getMenuItems = () => {
    const modoParaVerificar = modoActual || modo;
    if (modoParaVerificar === 'FACTURACION') {
      return billingMenuItems;
    }
    return posMenuItems;
  };

  const menuItems = getMenuItems();

  const isCurrentPath = (path) => {
    // Verificar rutas específicas primero
    if (path === '/pos') {
      // Solo es ruta POS si es exactamente /pos (no /pos/* ni /billing)
      return location.pathname === '/pos';
    }
    if (path.startsWith('/billing')) {
      return location.pathname.startsWith('/billing');
    }
    return location.pathname === path;
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleItemClick = (path) => {
    navigate(path);
  };

  const handleCambiarModo = () => {
    // Verificar si tiene acceso al otro módulo
    const currentModo = modoActual || modo;
    const targetModo = currentModo === 'FACTURACION' ? 'POS' : 'FACTURACION';
    
    // Verificar si tiene autorización para el otro módulo
    if (targetModo === 'POS' && !hasPOS) {
      return; // No tiene acceso a POS
    }
    if (targetModo === 'FACTURACION' && !hasFacturacion) {
      return; // No tiene acceso a Facturación
    }
    
    // Cambiamos el modo usando toggleModo
    toggleModo();
    
    // Navegamos según la ruta actual y el modo actual
    const currentPath = location.pathname;
    const targetPath = currentModo === 'FACTURACION' ? '/pos' : '/billing';
    
    if (currentPath.startsWith('/pos')) {
      navigate('/billing', { replace: true });
    } else if (currentPath.startsWith('/billing')) {
      navigate('/pos', { replace: true });
    } else {
      navigate(targetPath, { replace: true });
    }
  };

  return (
    <Drawer
      variant={isMobileDrawer ? 'temporary' : 'permanent'}
      sx={{
        width: isMobileDrawer ? DRAWER_WIDTH : (isMobile ? 0 : DRAWER_WIDTH),
        flexShrink: 0,
        display: isMobile && !isMobileDrawer ? 'none' : 'block',
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          backgroundColor: (modoActual || modo) === 'FACTURACION' ? '#ffffff' : 'primary.main',
          color: 'white',
        },
      }}
    >
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
          {companyLogo ? (
            <Box
              sx={{
                width: 140,
                height: 50,
                backgroundImage: `url(${companyLogo})`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                backgroundSize: 'contain',
                borderRadius: 1,
              }}
            />
          ) : (
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'white' }}>
              {(modoActual || modo) === 'FACTURACION' ? 'FACTURACIÓN' : 'NX2026'}
            </Typography>
          )}
        </Box>
      </Toolbar>
      <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
      <List sx={{ mt: 1, flex: 1 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              onClick={() => handleItemClick(item.path)}
              selected={isCurrentPath(item.path)}
              sx={{
                mx: 1,
                borderRadius: 1,
                // Touch target grande para móvil
                minHeight: isMobileDrawer ? 48 : 'auto',
                '&.Mui-selected': {
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.2)',
                  },
                },
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
                // Feedback visual al tocar
                '&:active': {
                  backgroundColor: 'rgba(255,255,255,0.25)',
                },
              }}
            >
              <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      
      <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
      
      {/* Botón para cambiar de modo - solo mostrar si tiene acceso al otro módulo */}
      {((modoActual || modo) === 'POS' && hasFacturacion) || ((modoActual || modo) === 'FACTURACION' && hasPOS) ? (
        <Box sx={{ p: 2 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleCambiarModo}
            startIcon={(modoActual || modo) === 'POS' ? <Receipt /> : <PointOfSale />}
            sx={{
              backgroundColor: (modoActual || modo) === 'POS' ? '#1565c0' : '#2e7d32',
              '&:hover': {
                backgroundColor: (modoActual || modo) === 'POS' ? '#0d47a1' : '#1b5e20',
              },
              textTransform: 'none',
              fontWeight: 'bold',
              py: 1.5,
              // Touch target grande
              minHeight: 48,
            }}
          >
            {(modoActual || modo) === 'POS' ? 'FACTURAR' : 'PUNTO DE VENTA'}
          </Button>
        </Box>
      ) : null}

      {/* Botón de Configuración */}
      <Box sx={{ p: 2, pt: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<Settings />}
          onClick={() => navigate('/settings')}
          sx={{
            borderColor: 'rgba(255,255,255,0.3)',
            color: 'white',
            '&:hover': {
              borderColor: 'white',
              backgroundColor: 'rgba(255,255,255,0.1)',
            },
            textTransform: 'none',
            minHeight: 48,
          }}
        >
          Configuración
        </Button>
      </Box>

      {/* Cerrar sesión */}
      <Box sx={{ p: 2, pt: 0 }}>
        <Button
          fullWidth
          variant="outlined"
          startIcon={<Logout />}
          onClick={handleLogout}
          sx={{
            borderColor: 'rgba(255,255,255,0.3)',
            color: 'white',
            '&:hover': {
              borderColor: 'white',
              backgroundColor: 'rgba(255,255,255,0.1)',
            },
            textTransform: 'none',
            minHeight: 48,
          }}
        >
          Cerrar Sesión
        </Button>
      </Box>

      {/* Switch para modo día/noche */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          px: 2,
          py: 1,
        }}
      >
        <LightMode sx={{ color: darkMode ? '#fff' : '#ffeb3b', fontSize: 20 }} />
        <Switch
          checked={darkMode}
          onChange={toggleDarkMode}
          sx={{
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: '#4fc3f7',
            },
            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
              backgroundColor: '#4fc3f7',
            },
          }}
        />
        <DarkMode sx={{ color: darkMode ? '#4fc3f7' : '#fff', fontSize: 20 }} />
      </Box>
    </Drawer>
  );
};

export default Sidebar;

export { DRAWER_WIDTH };
